import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Printer, Shield } from 'lucide-react';
import { useAppSelector } from '../store';
import { API_BASE_URL } from '../lib/utils';
import { usePermission } from '../components/shared/PermissionGuard';
import PrfAccessReasonModal, {
  getPrfAccessSession,
  storePrfAccessSession,
} from '../components/command/PrfAccessReasonModal';
import PrfRecordFullView from '../components/command/PrfRecordFullView';
import type { PatientReportForm } from '../types/prf';

type PrfAccessLog = {
  id: string;
  userId: string;
  username: string;
  userName?: string;
  reason: string;
  accessedAt: string;
};

type StoredPRF = Partial<PatientReportForm> & {
  id: string;
  incidentId: string;
  eventId?: string;
  submittedAt?: string;
  createdBy?: string;
  data?: Partial<PatientReportForm>;
  accessLogs?: PrfAccessLog[];
  printLogs?: Array<{ id: string; reason: string; printedAt: string; username: string }>;
};

export default function PRFDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAppSelector((state) => state.auth);
  const [prf, setPrf] = useState<StoredPRF | null>(null);
  const [currentAccess, setCurrentAccess] = useState<PrfAccessLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsReason, setNeedsReason] = useState(false);
  const [accessBusy, setAccessBusy] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  const canView = usePermission('prfs:view');
  const canPrint = usePermission('prfs:print');

  const loadPrf = async (accessId: string) => {
    if (!id || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/prfs/${encodeURIComponent(id)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-PRF-Access-Id': accessId,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data.requiresAccessReason) {
        setNeedsReason(true);
        setPrf(null);
        setCurrentAccess(null);
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Failed to load PRF');
      setPrf(data.prf);
      setCurrentAccess(data.currentAccess || null);
      setNeedsReason(false);
    } catch (e) {
      console.error('Failed to fetch PRF', e);
      setPrf(null);
      setNeedsReason(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id || !token || !canView) {
      setLoading(false);
      return;
    }
    const session = getPrfAccessSession(id);
    if (!session?.id) {
      setNeedsReason(true);
      setLoading(false);
      return;
    }
    loadPrf(session.id);
  }, [id, token, canView]);

  const confirmAccess = async (reason: string) => {
    if (!id || !token) return;
    setAccessBusy(true);
    setAccessError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/prfs/${encodeURIComponent(id)}/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to record access');
      storePrfAccessSession(id, {
        id: data.access.id,
        reason: data.access.reason,
        accessedAt: data.access.accessedAt,
        username: data.access.username,
      });
      setPrf(data.prf);
      setCurrentAccess(data.access);
      setNeedsReason(false);
    } catch (e) {
      setAccessError(e instanceof Error ? e.message : 'Failed to record access');
    } finally {
      setAccessBusy(false);
    }
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Access Denied</h2>
          <p className="text-sm text-muted-foreground">You do not have permission to view Patient Report Forms.</p>
        </div>
      </div>
    );
  }

  if (needsReason || (!prf && !loading)) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/prfs')} className="p-1.5 rounded-md hover:bg-accent transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Shield className="w-5 h-5 text-primary" />
          <div>
            <h1 className="text-base font-bold text-foreground">PRF access required</h1>
            <p className="text-xs text-muted-foreground font-mono">{id}</p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-sm text-muted-foreground text-center max-w-sm">
            Enter a justification to open this Patient Report Form. Your username, time, and reason will be stored against the record.
          </p>
        </div>
        <PrfAccessReasonModal
          open
          prfId={id || ''}
          username={user?.username || user?.email || '—'}
          busy={accessBusy}
          error={accessError}
          onCancel={() => navigate('/prfs')}
          onConfirm={confirmAccess}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-muted-foreground">Loading PRF...</p>
      </div>
    );
  }

  if (!prf) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-3">PRF not found</p>
          <button onClick={() => navigate(-1)} className="text-sm text-primary hover:underline">Go back</button>
        </div>
      </div>
    );
  }

  // Data may be nested under `data` (from POST) or at root
  const d = (prf.data || prf) as Partial<PatientReportForm> & Record<string, unknown>;
  const isParacetamolForm = d.formType === 'paracetamol_screening' || !!d.paracetamolScreening;
  const isMinorInjuryForm = d.formType === 'minor_injury' || prf.id.startsWith('MIF-');
  const formTitle = isParacetamolForm
    ? 'Paracetamol Screening Record'
    : isMinorInjuryForm
      ? 'Minor Injury Form'
      : 'Patient Report Form';

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <>
      <style>{`
        @media print {
          @page {
            margin: 15mm 15mm 20mm 15mm;
            size: A4;
            @top-center {
              content: "Event ID: ${prf.eventId || '—'} | Incident ID: ${prf.incidentId} | PRF ID: ${prf.id}";
              font-size: 8pt;
              color: #666;
              font-family: sans-serif;
            }
            @bottom-center {
              content: "CONFIDENTIAL | Printed by: ${user?.username || 'Unknown'} | Access reason: ${(currentAccess?.reason || '').replace(/"/g, "'")}";
              font-size: 8pt;
              color: #666;
              font-family: sans-serif;
            }
          }
          .print-section,
          .prf-print-section {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>
      <div className="flex flex-col h-full bg-background overflow-hidden print:bg-white print:font-sans print:text-gray-900">
        {/* Header */}
        <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3 flex items-center gap-3 print:hidden">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-md hover:bg-accent transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <FileText className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-foreground truncate">{prf.id}</h1>
            <p className="text-xs text-muted-foreground">
              Incident: <span className="font-mono">{prf.incidentId}</span>
              {prf.submittedAt && <> &middot; Submitted: {formatDate(prf.submittedAt)}</>}
              {prf.createdBy && <> &middot; By: {prf.createdBy}</>}
            </p>
          </div>
          {canPrint && (
            <button
              onClick={() => {
                const accessId = currentAccess?.id || (id ? getPrfAccessSession(id)?.id : null);
                if (id && token && accessId) {
                  fetch(`${API_BASE_URL}/prfs/${id}/print`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                      'X-PRF-Access-Id': accessId,
                    },
                    body: JSON.stringify({ accessId }),
                  }).catch(() => { /* ignore */ });
                }
                window.print();
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs font-medium"
              title="Print PRF"
            >
              <Printer className="w-3.5 h-3.5" />
              Print
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 print:p-4">
          <PrfRecordFullView
            prfId={prf.id}
            incidentId={prf.incidentId}
            eventId={prf.eventId}
            submittedAt={prf.submittedAt}
            submittedBy={prf.createdBy}
            data={d}
            accessLogs={prf.accessLogs}
            accessBanner={
              currentAccess
                ? {
                    username: currentAccess.username,
                    reason: currentAccess.reason,
                    accessedAt: currentAccess.accessedAt,
                    label: 'Access justification (this session)',
                  }
                : undefined
            }
            showPrintHeader
          />
        </div>

        <div className="hidden print:flex print:flex-col print:gap-1 print:px-5 print:py-3 print:bg-gray-100 print:border-t print:border-gray-300 print:text-xs print:text-gray-600 print:mt-8">
            <div className="print:flex print:justify-between print:items-center">
              <span className="font-semibold text-red-600 print:text-red-600 print:uppercase print:tracking-wider">CONFIDENTIAL</span>
              <span>Printed by: {user?.username || 'Unknown'}</span>
        </div>
        {currentAccess && (
          <p>
            Access justification: {currentAccess.username} at {formatDate(currentAccess.accessedAt)} — {currentAccess.reason}
          </p>
        )}
        </div>
      </div>
    </>
  );
}
